/**
 * The models this app runs. What is written here is only where it starts from: the server asks xAI which
 * models the account offers and reads each one's own page (context window, reasoning efforts, prices), keeps
 * that list in the database and hands it to the app (server/lib/model-catalog.ts → setCatalog). A model
 * that is no longer offered falls back to the newest; nothing here has to be edited when xAI ships a model.
 *
 * Verified against LIVE docs.x.ai and the account's own model list (2026-10-04):
 *   - grok-4.7 (released 2026-09-02, marked "Latest"): 500,000 context; $2.00 / $0.50 cached / $6.00 per
 *     1M tokens, higher-context pricing past 200K; reasoning efforts low, medium, high (default), xhigh
 *     (model page /developers/models/grok-4.7; GET /v1/language-models gives the same prices).
 *
 * Verified against LIVE docs.x.ai (re-checked in-browser 2026-08-31, second pass):
 *   - grok-4.6: 500k context; $2 / $0.50 cached / $6 per 1M tokens under 200k prompt
 *     tokens, $4 / $1 / $12 at ≥200k ("long context"). Reasoning guide page: effort
 *     low|medium|high(default)|xhigh, "Reasoning cannot be disabled" → no 'none'.
 *   - grok-4.3: 1M context; $1.25 / $0.20 cached / $2.50 under 200k, 2× at ≥200k
 *     (model detail page /developers/models/grok-4.3). REST spec
 *     (/developers/rest-api-reference/inference/chat.md, POST /v1/responses →
 *     `reasoning.effort`): "Only supported by grok-4.3. Possible values are `none`
 *     (disables reasoning completely), `low` (this is the default if not specified),
 *     `medium` and `high`." The May-15-2026 retirement page confirms: grok-4.3 is the
 *     MIGRATION TARGET (not retired) with "4 reasoning effort levels (none, low,
 *     medium, and high)".
 *   - grok-4.5 is intentionally ABSENT: same price as 4.6 with fewer capabilities.
 */

export interface ModelSpec {
  id: string
  label: string
  /** One-line description for the picker UI. */
  blurb: string
  /** Total context window (tokens). */
  context: number
  /** Prompt-token threshold where xAI switches the request to long-context (2×) pricing. */
  longContextAt: number
  /** USD per 1M tokens — short-context rates (long context is 2× each, per docs). */
  price: { input: number; cachedInput: number; output: number }
  /** reasoning.effort values the model supports. */
  efforts: readonly ReasoningEffort[]
  /** When xAI released it (ms): the newest is what an installation uses unless something else was chosen. */
  created?: number
  /** One sentence of xAI's own about it. */
  about?: string
}

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh'

export const MODELS: Record<string, ModelSpec> = {
  'grok-4.3': {
    created: Date.UTC(2026, 3, 17),
    id: 'grok-4.3',
    label: 'Grok 4.3',
    blurb: 'Fast and economical · 1M context',
    context: 1_000_000,
    longContextAt: 200_000,
    price: { input: 1.25, cachedInput: 0.2, output: 2.5 },
    efforts: ['none', 'low', 'medium', 'high'] as const,
  },
  'grok-4.7': {
    created: Date.UTC(2026, 8, 2),
    id: 'grok-4.7',
    label: 'Grok 4.7',
    blurb: 'Newest and strongest · reasoning up to xhigh · 500k context',
    context: 500_000,
    longContextAt: 200_000,
    price: { input: 2.0, cachedInput: 0.5, output: 6.0 },
    efforts: ['low', 'medium', 'high', 'xhigh'] as const,
  },
  'grok-4.6': {
    created: Date.UTC(2026, 7, 6),
    id: 'grok-4.6',
    label: 'Grok 4.6',
    blurb: 'The one before · reasoning up to xhigh · 500k context',
    context: 500_000,
    longContextAt: 200_000,
    price: { input: 2.0, cachedInput: 0.5, output: 6.0 },
    efforts: ['low', 'medium', 'high', 'xhigh'] as const,
  },
}

export type ModelId = string

/** The ids on offer, newest first. (The same array object is kept when the list changes: whoever imported it sees it change.) */
export const MODEL_IDS: ModelId[] = []
/** What is used when nothing else was chosen, or what was chosen is no longer offered: the newest. */
export let DEFAULT_MODEL: ModelId = 'grok-4.7'

const newestFirst = (a: ModelSpec, b: ModelSpec) => (b.created ?? 0) - (a.created ?? 0) || b.id.localeCompare(a.id, 'en', { numeric: true })
/** Take a fresh list (from xAI, by way of the server): it replaces what was known. An empty list changes nothing. */
export function setCatalog(list: ModelSpec[]): void {
  const good = list.filter((m) => m && typeof m.id === 'string' && m.context > 0 && m.efforts?.length)
  if (!good.length) return
  for (const k of Object.keys(MODELS)) delete MODELS[k]
  for (const m of [...good].sort(newestFirst)) MODELS[m.id] = m
  MODEL_IDS.splice(0, MODEL_IDS.length, ...Object.keys(MODELS))
  DEFAULT_MODEL = MODEL_IDS[0]
}
/** The list as it stands, newest first. */
export const catalog = (): ModelSpec[] => MODEL_IDS.map((id) => MODELS[id])
setCatalog(Object.values(MODELS))

/** Coerce any string (env var, request body, stored chat column) to a registry model. */
export function resolveModel(id: string | null | undefined): ModelSpec {
  return (id && MODELS[id]) || MODELS[DEFAULT_MODEL]
}

/** Highest effort the model supports (heavy-mode synthesis wants the ceiling). */
export function maxEffort(model: ModelSpec): ReasoningEffort {
  return model.efforts[model.efforts.length - 1]
}

const EFFORT_ORDER: ReasoningEffort[] = ['none', 'low', 'medium', 'high', 'xhigh']

/** Clamp a requested effort to what the model supports: the HIGHEST supported level
 *  that does not exceed the request (medium→low on grok-4.3, xhigh→high on grok-4.3),
 *  falling back to the model's lowest level. */
export function clampEffort(model: ModelSpec, effort: ReasoningEffort): ReasoningEffort {
  if (model.efforts.includes(effort)) return effort
  const want = EFFORT_ORDER.indexOf(effort)
  for (let i = want; i >= 0; i--) {
    if (model.efforts.includes(EFFORT_ORDER[i])) return EFFORT_ORDER[i]
  }
  return model.efforts[0]
}

/**
 * Where the in-turn auto-compaction should trigger. Quality-first policy: let the
 * context grow (paying long-context rates is accepted) and compact only when the
 * window itself is at risk — 75% of the model's real context, with a hard floor so
 * a misconfigured override can never push past the window.
 */
export function compactAtTokens(model: ModelSpec): number {
  // Guarded: this module is shared with the browser bundle, where `process` may not exist.
  const envRatio = typeof process !== 'undefined' ? process.env?.AI_COMPACT_RATIO : undefined
  const ratioRaw = Number(envRatio ?? 0.75)
  const ratio = Math.min(0.9, Math.max(0.3, Number.isFinite(ratioRaw) ? ratioRaw : 0.75))
  return Math.min(Math.round(model.context * ratio), model.context - 60_000)
}
